import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Webhook, Plus, Pencil, Trash2, RotateCcw,
  CheckCircle2, XCircle, Clock, Eye,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import toast from 'react-hot-toast';

const SUPPORTED_EVENTS = [
  'sale.created',
  'sale.voided',
  'sale.refunded',
  'purchase.received',
  'low_stock.alert',
  'customer.created',
  'stock_transfer.completed',
] as const;

interface WebhookRecord {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
  lastDeliveryAt?: string;
  successCount?: number;
  failureCount?: number;
  secret?: string;
}

interface WebhookDelivery {
  id: string;
  event: string;
  statusCode?: number;
  success: boolean;
  createdAt: string;
  durationMs?: number;
  responseBody?: string;
}

const emptyForm = () => ({
  name: '',
  url: '',
  events: [] as string[],
  isActive: true,
});

export function WebhooksPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('api:manage') || hasPermission('api:*');
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editWebhook, setEditWebhook] = useState<WebhookRecord | null>(null);
  const [deliveriesWebhook, setDeliveriesWebhook] = useState<WebhookRecord | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmRotateId, setConfirmRotateId] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  const { data: webhooks, isLoading } = useQuery<WebhookRecord[]>({
    queryKey: ['webhooks'],
    queryFn: () => api.get('/webhooks').then(r => r.data?.data ?? r.data),
  });

  const { data: deliveriesRes, isLoading: deliveriesLoading } = useQuery<WebhookDelivery[] | { data: WebhookDelivery[] }>({
    queryKey: ['webhook-deliveries', deliveriesWebhook?.id],
    queryFn: () => api.get(`/webhooks/${deliveriesWebhook!.id}/deliveries`).then(r => r.data),
    enabled: !!deliveriesWebhook,
  });
  const deliveries: WebhookDelivery[] = Array.isArray(deliveriesRes) ? deliveriesRes : (deliveriesRes?.data ?? []);

  const createMutation = useMutation({
    mutationFn: (data: ReturnType<typeof emptyForm>) => api.post('/webhooks', data),
    onSuccess: () => {
      toast.success('Webhook created');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      setCreateOpen(false);
      setForm(emptyForm());
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to create webhook'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReturnType<typeof emptyForm>> }) =>
      api.patch(`/webhooks/${id}`, data),
    onSuccess: () => {
      toast.success('Webhook updated');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      setEditWebhook(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to update webhook'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      toast.success('Webhook deleted');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to delete webhook'),
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/webhooks/${id}/rotate-secret`).then(r => r.data),
    onSuccess: (data: { secret?: string }) => {
      toast.success('Secret rotated');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      if (data?.secret) setNewSecret(data.secret);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err.response?.data?.message ?? 'Failed to rotate secret'),
  });

  const toggleEvent = (event: string, target: 'create' | 'edit') => {
    if (target === 'create') {
      setForm(f => ({
        ...f,
        events: f.events.includes(event) ? f.events.filter(e => e !== event) : [...f.events, event],
      }));
    } else if (editWebhook) {
      setEditWebhook(w => w ? ({
        ...w,
        events: w.events.includes(event) ? w.events.filter(e => e !== event) : [...w.events, event],
      }) : null);
    }
  };

  const handleCreate = () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.url.trim()) { toast.error('URL is required'); return; }
    if (form.events.length === 0) { toast.error('Select at least one event'); return; }
    createMutation.mutate(form);
  };

  const handleUpdate = () => {
    if (!editWebhook) return;
    if (!editWebhook.name.trim()) { toast.error('Name is required'); return; }
    if (!editWebhook.url.trim()) { toast.error('URL is required'); return; }
    if (editWebhook.events.length === 0) { toast.error('Select at least one event'); return; }
    updateMutation.mutate({
      id: editWebhook.id,
      data: { name: editWebhook.name, url: editWebhook.url, events: editWebhook.events, isActive: editWebhook.isActive },
    });
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Webhooks"
        subtitle="Receive real-time HTTP notifications for system events"
        action={canManage && (
          <Button onClick={() => { setForm(emptyForm()); setCreateOpen(true); }} className="gap-2">
            <Plus className="h-4 w-4" /> Add Webhook
          </Button>
        )}
      />

      <Card>
        {isLoading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : !webhooks?.length ? (
          <CardContent className="pt-4">
            <EmptyState
              icon={Webhook}
              title="No webhooks configured"
              description="Add a webhook endpoint to receive real-time event notifications"
              action={canManage ? { label: 'Add Webhook', onClick: () => { setForm(emptyForm()); setCreateOpen(true); } } : undefined}
            />
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tblName')}</Th>
                  <Th>{t('tblUrl')}</Th>
                  <Th>{t('tblEvents')}</Th>
                  <Th className="text-center">{t('tblStatus')}</Th>
                  <Th>{t('tblCreated')}</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {webhooks.map((wh) => (
                  <Tr key={wh.id}>
                    <Td className="font-medium text-sm" label={t('tblName')}>{wh.name}</Td>
                    <Td className="text-sm text-muted-foreground font-mono max-w-xs truncate" label={t('tblUrl')} title={wh.url}>
                      {wh.url}
                    </Td>
                    <Td label={t('tblEvents')}>
                      <div className="flex flex-wrap gap-1">
                        {wh.events.map(ev => (
                          <span key={ev} className="inline-flex text-xs bg-info/10 text-info-text px-1.5 py-0.5 rounded font-mono">
                            {ev}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td className="text-center" label={t('tblStatus')}>
                      {wh.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-success/10 text-success-text px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          <XCircle className="h-3 w-3" /> Inactive
                        </span>
                      )}
                    </Td>
                    <Td className="text-sm text-muted-foreground" label={t('tblCreated')}>{formatDate(wh.createdAt)}</Td>
                    <Td className="td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'history',
                            label: 'Delivery history',
                            icon: Eye,
                            onSelect: () => setDeliveriesWebhook(wh),
                            primary: true,
                          },
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: Pencil,
                            onSelect: () => setEditWebhook({ ...wh }),
                            permission: canManage,
                            primary: true,
                          },
                          {
                            key: 'rotate',
                            label: 'Rotate secret',
                            icon: RotateCcw,
                            onSelect: () => setConfirmRotateId(wh.id),
                            permission: canManage,
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: Trash2,
                            onSelect: () => setConfirmDeleteId(wh.id),
                            permission: canManage,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <FormDialog
        open={createOpen}
        title="Add Webhook"
        width="520px"
        submitLabel="Create Webhook"
        isPending={createMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); handleCreate(); }}
        onClose={() => setCreateOpen(false)}
      >
        <WebhookForm
          name={form.name}
          url={form.url}
          events={form.events}
          isActive={form.isActive}
          onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
          onToggleEvent={(ev) => toggleEvent(ev, 'create')}
        />
      </FormDialog>

      {/* Edit Dialog */}
      <FormDialog
        open={!!editWebhook}
        title="Edit Webhook"
        width="520px"
        submitLabel="Save Changes"
        isPending={updateMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}
        onClose={() => setEditWebhook(null)}
      >
        {editWebhook && (
          <WebhookForm
            name={editWebhook.name}
            url={editWebhook.url}
            events={editWebhook.events}
            isActive={editWebhook.isActive}
            onChange={(field, value) => setEditWebhook(w => w ? { ...w, [field]: value } : null)}
            onToggleEvent={(ev) => toggleEvent(ev, 'edit')}
          />
        )}
      </FormDialog>

      {/* Delivery History Dialog */}
      <Dialog
        visible={!!deliveriesWebhook}
        onHide={() => setDeliveriesWebhook(null)}
        header={`Delivery History — ${deliveriesWebhook?.name ?? ''}`}
        modal
        breakpoints={{ '960px': '80vw', '640px': '95vw' }}
        style={{ width: '640px' }}
      >
        {deliveriesLoading ? (
          <SkeletonTable rows={5} cols={4} />
        ) : !deliveries?.length ? (
          <div className="py-8 text-center">
            <Clock className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No deliveries recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tblEvent')}</Th>
                  <Th className="text-center">{t('tblStatus')}</Th>
                  <Th className="text-end">{t('tblHttp')}</Th>
                  <Th className="text-end">{t('tblDuration')}</Th>
                  <Th>{t('tblTime')}</Th>
                </Tr>
              </THead>
              <TBody>
                {deliveries.map((d) => (
                  <Tr key={d.id}>
                    <Td className="font-mono text-xs" label={t('tblEvent')}>{d.event}</Td>
                    <Td className="text-center" label={t('tblStatus')}>
                      {d.success ? (
                        <CheckCircle2 className="h-4 w-4 text-success-text mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive-text mx-auto" />
                      )}
                    </Td>
                    <Td className="text-end text-xs font-mono" label={t('tblHttp')}>
                      <span className={d.statusCode && d.statusCode < 300 ? 'text-success-text' : 'text-destructive-text'}>
                        {d.statusCode ?? '—'}
                      </span>
                    </Td>
                    <Td className="text-end text-xs text-muted-foreground" label={t('tblDuration')}>
                      {d.durationMs != null ? `${d.durationMs}ms` : '—'}
                    </Td>
                    <Td className="text-xs text-muted-foreground" label={t('tblTime')}>{formatDate(d.createdAt)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Dialog>

      {/* New Secret Dialog */}
      <Dialog
        visible={!!newSecret}
        onHide={() => setNewSecret(null)}
        header="New Webhook Secret"
        modal
        style={{ width: '460px' }}
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setNewSecret(null)}>Done</Button>
          </div>
        }
      >
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Copy this secret now — it will not be shown again. Use it to verify webhook signatures.
          </p>
          <div className="bg-muted rounded-lg p-3 font-mono text-sm break-all select-all border border-border">
            {newSecret}
          </div>
          <Button variant="outline" size="sm" className="gap-2"
            onClick={() => { navigator.clipboard.writeText(newSecret ?? ''); toast.success('Copied!'); }}>
            Copy to clipboard
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) deleteMutation.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
        title="Delete Webhook"
        description="This will permanently delete the webhook and stop all deliveries. This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />

      <ConfirmDialog
        open={!!confirmRotateId}
        onClose={() => setConfirmRotateId(null)}
        onConfirm={() => {
          if (confirmRotateId) rotateMutation.mutate(confirmRotateId);
          setConfirmRotateId(null);
        }}
        title="Rotate Webhook Secret"
        description="This will generate a new signing secret. Your existing integration will stop working until you update the secret in your application."
        confirmLabel="Rotate Secret"
        variant="danger"
      />
    </div>
  );
}

interface WebhookFormProps {
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  onChange: (field: string, value: string | boolean) => void;
  onToggleEvent: (event: string) => void;
}

function WebhookForm({ name, url, events, isActive, onChange, onToggleEvent }: WebhookFormProps) {
  return (
    <div className="space-y-4 pt-2">
      <div>
        <Label className="block mb-1">Name *</Label>
        <Input
          maxLength={80}
          value={name}
          onChange={e => onChange('name', e.target.value)}
          placeholder="My Webhook"
        />
      </div>
      <div>
        <Label className="block mb-1">Endpoint URL *</Label>
        <Input
          maxLength={500}
          value={url}
          onChange={e => onChange('url', e.target.value)}
          className="font-mono"
          placeholder="https://example.com/webhook"
          type="url"
        />
      </div>
      <div>
        <Label className="block mb-2">Events to Subscribe *</Label>
        <div className="grid grid-cols-1 gap-2">
          {SUPPORTED_EVENTS.map(ev => (
            <Label key={ev} className="flex items-center gap-2.5 cursor-pointer rounded-lg border bg-background p-2.5 font-normal leading-normal text-foreground hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={events.includes(ev)}
                onChange={() => onToggleEvent(ev)}
              />
              <span className="font-mono text-xs text-foreground">{ev}</span>
            </Label>
          ))}
        </div>
      </div>
      <Label className="flex items-center gap-2.5 cursor-pointer font-normal leading-normal text-foreground">
        <Checkbox
          checked={isActive}
          onChange={e => onChange('isActive', e.target.checked)}
        />
        <span className="text-sm font-medium">Active</span>
        <span className="text-xs text-muted-foreground ms-1">(deliver events immediately)</span>
      </Label>
    </div>
  );
}
